import { defineStore, storeToRefs } from 'pinia'
import type { ProjectSpeckle } from '../types/projectSpeckle.type'
import { useApi } from '@/api/config/useApi'
import API from '@/api/config/apiUrls'
import { ref } from 'vue'
import { useSpeckleAuthStore, useSpeckleStore } from '@haskoning/vue-speckle'
import type { SpeckleModelMutation } from '@haskoning/js-speckle'

const { api } = useApi()

const service = API.CORE.SPECKLE.PROJECT_SPECKLE.INDEX

export const useProjectSpeckleStore = defineStore('projectSpeckleStore', () => {
  const currentItem = ref<ProjectSpeckle | null>(null)
  const writeLoading = ref<boolean>(false)
  const fetchLoading = ref<boolean>(false)
  const message = ref<string>('')
  
  const speckleStore = useSpeckleStore({ autoSelectData: false })

  const resetRelatedSpeckleData = () => {
    speckleStore.currentSelection.project = undefined
    speckleStore.currentSelection.model = undefined
    speckleStore.currentSelection.version = undefined
    speckleStore.allProjects = []
    speckleStore.allModels = []
    speckleStore.allVersions = []
    currentItem.value = null
    message.value = ''
  }

  const fetchProjectSpeckle = async (projectId: number) => {
    const response = await api.service(service).find({
        query: { search: projectId },
      })
    if (response) {
      currentItem.value = response.data[0] as unknown as ProjectSpeckle
      const projectSpeckleId = currentItem.value.project_id
      const { currentSelection, allProjects } = storeToRefs(speckleStore)
      const project = allProjects.value.find((proj) => proj.id === projectSpeckleId)
      currentSelection.value.project = project || undefined
    }
  }

  const addDefaultModel = async (speckleProjectId: string, modelName: string) => {
    const { allModels } = storeToRefs(speckleStore)

    const index = allModels.value.findIndex((model) => model.name === modelName)
    if (index === -1 && modelName) {
      const defaultModel: SpeckleModelMutation = {
        name: modelName,
        description: '',
        projectId: speckleProjectId
      }
      const newModel = await speckleStore.createSpeckleProjectModel(defaultModel)
      return newModel
    }
    return allModels.value[index]
  }

  const updateProjectSpeckle = async () => {
    if (currentItem.value && speckleStore.currentSelection.project) {
      const speckleProjectId = speckleStore.currentSelection.project.id as unknown as string
      if (speckleProjectId === currentItem.value.project_id) return
      currentItem.value.project_id = speckleProjectId
      try {
        writeLoading.value = true
        const response = await api.service(API.CORE.SPECKLE.PROJECT_SPECKLE.INDEX).patch(currentItem.value.id, currentItem.value)
        currentItem.value = response
        writeLoading.value = false
      } catch (error) {
        console.error('Failed to update project speckle:', error)
      }
    }
  }

  const send = async (calculationId: number, message: string) => {
    const speckleAuthStore = useSpeckleAuthStore()
    if (!speckleStore.currentSelection.model) return
    const model_name = speckleStore.currentSelection.model.name
    const payload = {
      project: currentItem.value?.linked_model,
      message,
      token: speckleAuthStore.token,
      model_name
    }
    const url = API.CORE.SPECKLE.COMMUNICATION_SPECKLE.SEND(calculationId)
    writeLoading.value = true
    await api.service(url).post(payload)
    writeLoading.value = false
  }

  return {
    currentItem,
    writeLoading,
    fetchLoading,
    message,
    send,
    updateProjectSpeckle,
    fetchProjectSpeckle,
    addDefaultModel,
    resetRelatedSpeckleData
  }
})
